/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import Restricted from '../../authorization/Restricted';
import AuthorizationContext from '../../authorization/AuthorizationContext';
import LeftBarMainItem from '../LeftBarMainItem';
import itemIsSelected from '../utils/SelectedItems';

// Helper function to recursively check if a section contains the active item
const sectionContainsActiveItem = (itemList, currentPathname) => {
  for (const subItem of itemList) {
    // Check if the item itself is selected
    if (subItem.path && itemIsSelected(subItem.path, currentPathname)) {
      return true;
    }
    // If the item has sub-items, recursively check them
    if (subItem.items && sectionContainsActiveItem(subItem.items, currentPathname)) {
      return true;
    }
  }
  return false;
};

// Find the first leaf item (with a path) the user has permission for.
// Falls back to the first leaf path if no permission info is resolvable.
const findFirstAllowedLeafPath = async (itemList, isAllowedTo) => {
  for (const subItem of itemList) {
    if (subItem.path) {
      if (!subItem.permission) return subItem.path;
      try {
        // eslint-disable-next-line no-await-in-loop
        const allowed = await isAllowedTo(subItem.permission);
        if (allowed) return subItem.path;
      } catch {
        // ignore and continue
      }
    }
    if (subItem.items) {
      // eslint-disable-next-line no-await-in-loop
      const nested = await findFirstAllowedLeafPath(subItem.items, isAllowedTo);
      if (nested) return nested;
    }
  }
  return null;
};

export default function ExpandableMenuSection({
  name, items, permissions, isActive, onToggle, icon, collapsed, onExpandSidebar,
}) {
  const [open, setOpen] = React.useState(false);
  const [manuallyToggled, setManuallyToggled] = React.useState(false);
  const navigate = useHistory();
  const { isAllowedTo } = useContext(AuthorizationContext);

  const handleClick = async () => {
    if (collapsed && onExpandSidebar) {
      onExpandSidebar();
      setOpen(true);
      setManuallyToggled(true);
      const firstPath = await findFirstAllowedLeafPath(items, isAllowedTo);
      if (firstPath) navigate.push(firstPath);
      return;
    }
    setOpen(!open);
    setManuallyToggled(true); // Mark as manually toggled
  };

  const { pathname } = useLocation();

  useEffect(() => {
    const shouldBeOpen = sectionContainsActiveItem(items, pathname);
    
    // Only auto-manage if not manually toggled, or if we need to open because of active item
    if (shouldBeOpen && !open) {
      setOpen(true);
      setManuallyToggled(false); // Reset manual flag when auto-opening
    } else if (!shouldBeOpen && !manuallyToggled) {
      // Only auto-close if user hasn't manually toggled
      setOpen(false);
    }
  }, [pathname, items, open, manuallyToggled]);

  return (
    <Restricted to={permissions}>
      <div className="flex flex-col">
        <button
          type="button"
          onClick={handleClick}
          title={collapsed ? name : undefined}
          className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} w-full py-2 px-2 text-left text-md font-normal hover:bg-gray-200 focus:outline-none ${isActive ? 'bg-sky-100 hover:bg-sky-100' : ''}`}
        >
          {collapsed ? (
            icon && <span className="min-w-[24px]">{icon}</span>
          ) : (
            <>
              <span className="normal-case">{name}</span>
              {open ? (
                <ChevronUpIcon className="h-5 w-5" />
              ) : (
                <ChevronDownIcon className="h-5 w-5" />
              )}
            </>
          )}
        </button>
        {open && !collapsed && (
          <div className="pl-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
            {items.map((item) => (
              item.items ? (
                <ExpandableMenuSection
                  key={item.name}
                  name={item.name}
                  items={item.items}
                  permissions={item.permissions || []} // Permissions for the sub-section header
                  isActive={false} // Sub-sections don't get the top-level 'isActive' highlight
                  onToggle={onToggle} // Pass down the onToggle from LeftBar (for active section mgmt / drawer close)
                  icon={item.icon} // Pass icon for potential future use in sub-section button
                />
              ) : (
                <LeftBarMainItem
                  key={item.name}
                  name={item.name}
                  path={item.path}
                  permission={item.permission}
                  icon={item.icon}
                  closeDrawer={onToggle}
                />
              )
            ))}
          </div>
        )}
      </div>
    </Restricted>
  );
}

ExpandableMenuSection.propTypes = {
  name: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired, // Can now contain objects with their own 'items' array for nesting
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  isActive: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  icon: PropTypes.node, // Optional icon for the section header
  collapsed: PropTypes.bool,
  onExpandSidebar: PropTypes.func,
};

ExpandableMenuSection.defaultProps = {
  icon: null,
  collapsed: false,
  onExpandSidebar: undefined,
};
